// Based on TiMessengerTestTreiber.yaml 2.0.0
import { inject, Injectable } from '@angular/core';
import { MatrixService } from '../matrix/matrix.service';
import { Contact } from '../../../shared-api/contact';
import { MatrixError, Method } from 'matrix-js-sdk';
import { Contacts } from '../../../shared-api/contacts';
import { ContactManagementService as ApiContactManagementService } from '../../../shared-api/contactManagement.service';
import { matrixErrorAsJson } from '../../errors/error-with-status';

@Injectable({
  providedIn: 'root'
})
/**
 * Der ContactManagementService stellt das Adressbuch bereit.
 * Implementation of the mandatory ContactManagment API of the gematik TI-M Testtreiber
 * Umgesetze AFOs:
 * A_25363 - Testtreiber-Modul
 * A_25361 - keine Modifikation der Inhalte
 */
export class ContactManagementService implements ApiContactManagementService {
  private readonly matrix = inject(MatrixService);

  /**
   * Creates the setting for the contact
   * @param contact
   */
  async createContactSetting(contact: Contact): Promise<Contact> {
    if (!contact) {
      throw new Error('Required parameter contact was null or undefined when calling createContactSetting.');
    }

    try {
      return await this.matrix.client.http.authedRequest(Method.Post, '/contacts', undefined, contact);
    } catch (error) {
      console.error('createContactSetting: HTTP-Authed-Request fehlgeschlagen', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('CreateContactSettingError', error);
    }
  }

  /**
   * Deletes the setting for the contact {mxid}.
   * @param mxid ID of the contact (mxid).
   */

  async deleteContactSetting(mxid: string): Promise<void> {
    if (!mxid) {
      throw new Error('Required parameter mxid was null or undefined when calling deleteContactSetting.');
    }
    mxid = encodeURIComponent(mxid);
    try {
      return await this.matrix.client.http.authedRequest(Method.Post, `/contacts/${mxid}`);
    } catch (error) {
      console.error('deleteContactSetting: HTTP-Authed-Request fehlgeschlagen', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('DeleteContactSettingError', error);
    }
  }

  /**
   * Returns the contact with invite setting.
   * @param mxid ID of the contact (mxid).
   */

  async getContact(mxid: string): Promise<Contact | undefined> {
    if (!mxid) {
      throw new Error('Required parameter mxid was null or undefined when calling getContact.');
    }
    mxid = encodeURIComponent(mxid);
    try {
      return await this.matrix.client.http.authedRequest(Method.Post, `/contacts/${mxid}`);
    } catch (error) {
      console.error('getContact: HTTP-Authed-Request fehlgeschlagen', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('GetContactError', error);
    }
  }

  /**
   * Returns the contacts with invite settings.
   */

  async getContacts(): Promise<Contacts> {
    try {
      return await this.matrix.client.http.authedRequest(Method.Post, '/contacts');
    } catch (error) {
      console.error('getContacts: HTTP-Authed-Request fehlgeschlagen', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('GetContactsError', error);
    }
  }

  /**
   * Updates the setting for the contact.
   * @param contact
   */
  async updateContactSetting(contact: Contact): Promise<Contact> {
    if (!contact) {
      throw new Error('Required parameter contact was null or undefined when calling updateContactSetting.');
    }

    try {
      return await this.matrix.client.http.authedRequest(Method.Put, '/contacts', undefined, contact);
    } catch (error) {
      console.error('getContacts: HTTP Authed-Request fehlgeschlagen', error);

      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('GetContactsError', error);
    }
  }
}
